/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : TopBar.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/* global globalThis */
import {
  Box,
  DialogContentText,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { styled, alpha } from "@mui/material/styles";
import { sideBarData } from "../SideBar/sideBarData";
import actionType from "../../redux/actionTypes";
import { useDispatch, useSelector } from "react-redux";
import DeloitteLogo from "../../assets/logoIcons/Light Deloitte Logo.jpg";
import DeloitteLogoWhite from "../../assets/logoIcons/DeloitteWhite.png";
import MenuIcon from "@mui/icons-material/Menu";
import { HandleApiActions } from "../../redux/actions/actionHandler";
import profileActions from "../../redux/actions/profile";
import appTheme from "../../assets/app-theme/appTheme";
import customConsole from "../../config/customConsole";
import NotificationIcon from "../TopBar/NotificationIcon";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";

function TopBar() {
  const dispatch = useDispatch();

  const profilePicName = useSelector(
    (state) => state?.profileReducer?.profile_pic_name
  );
  const profilePicPath = useSelector(
    (state) => state?.profileReducer?.profile_pic_path
  );
  const fullname = useSelector(
    (state) => state?.profileReducer?.profile?.fullname
  );

  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const [openDrawer, setOpenDrawer] = React.useState(false);
  const [isLogout, setIsLogout] = React.useState(false);
  const [pageUrl, setPageUrl] = React.useState("PROFILE");
  const [profilePng, setProfilePng] = React.useState("");
  const [openLogOutConfirmDialog, setOpenLogOutConfirmDialog] = useState(false);

  React.useEffect(() => {
    customConsole("profilePicName updated");
    customConsole(profilePicName);
    setProfilePng(profilePicName);
  }, [profilePicName]);

  React.useEffect(() => {
    customConsole("profilePicPath updated");
    customConsole(profilePicPath);
  }, [profilePicPath]);

  React.useEffect(() => {
    if (profilePng?.length) {
      dispatch(
        HandleApiActions({
          ...profileActions.get_profile_pic,
          show_toast: true,
          file_name: profilePng,
          is_file: true,
        })
      );
    }
  }, [dispatch, profilePng]);


  useEffect(() => {
    const pathname = globalThis.location.pathname;
    customConsole(pathname);
    setPageUrl(pathname);
    customConsole("TOP BAR");
  }, []);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogOutConfirm = () => {
    localStorage.clear();
    sessionStorage.clear();

    setIsLogout(true);
    dispatch({
      type: actionType.SIGN_OUT,
    });
    dispatch({
      type: actionType.RESET_PROFILE,
    });
    dispatch({
      type: actionType.RESET_LAB_CAR_DATA,
    });
    dispatch({
      type: actionType.RESET_PROJECT_CONFIG,
    });
    navigate("/login", { replace: true });
  };

  const handleLogOut = () => {
    setOpenLogOutConfirmDialog(true);
  };
  if (isLogout) {
    return <Navigate to="/authenticate" />;
  }
  const toggleDrawer = () => {
    setOpenDrawer(!openDrawer);
  };
  const handleClickToPage = (to) => {
    navigate(to);
    setOpenDrawer(!openDrawer);
  };

  return (
    <div>
      <Box
        display="flex"
        justifyContent="space-between"
        padding="0px 15px"
        sx={{
          border: "0.5px solid",
          boxShadow:
            "4px 4px 10px rgba(0, 0, 0, 0.2), -4px -4px 10px rgba(0, 0, 0, 0.1)",
          minHeight: "50px",
        }}
        backgroundColor={appTheme.primaryColor}
      >
        {isMobile && (
          <Box>
            <IconButton onClick={toggleDrawer}>
              <MenuIcon sx={{ color: "white" }} />
            </IconButton>
            <Drawer open={openDrawer}>
              <Box ml="10px" mt="10px">
                <img alt="" src={DeloitteLogo} style={{ width: "210px" }} />
              </Box>
              <Divider />
              <Box sx={{ flex: 1, overflowY: "auto" }}>
                <List component="nav">
                  {sideBarData?.map((data) => (
                    <ListItemButton
                      key={data.path || data.title}
                      onClick={() => handleClickToPage(data.path)}
                      sx={{
                        backgroundColor: pageUrl === data.path ? "#cff5e8" : "",
                      }}
                    >
                      <ListItemIcon>{data.icon}</ListItemIcon>
                      <ListItemText primary={data.title} />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Drawer>
          </Box>
        )}
        {!isMobile && (
          <Box
            ml="20px"
            my="5px"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
            }}
          >
            <Box sx={{ position: "relative", display: "inline-block" }}>
              <img
                alt=""
                src={DeloitteLogoWhite}
                style={{ width: "100px" }}
              />
            </Box>
            <Box
              sx={{
                width: "1px",
                height: "20px",
                backgroundColor: "#53565A",
                mx: "15px",
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontStyle: "Open Sans",
                fontSize: "20px",
                color: "white",
                fontWeight: "normal",
                letterSpacing: "0.5px",
              }}
            >
              Digital Compliance
            </Typography>
          </Box>
        )}



        <Box display={"flex"} alignItems="center">
          <Box sx={{ mr: "10px" }}>
            <NotificationIcon />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", mr: "20px" }}>
            <IconButton
              id="user-profile-button"
              aria-controls={open ? "user-profile-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "8px 12px",
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                },
              }}
            >
              <Box
                sx={{
                  width: "30px",
                  height: "30px",
                  backgroundColor: "#D0D0CE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: "1px solid #ddd",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontStyle: "Open Sans",
                    fontSize: "16px",
                    color: "#000000",
                    fontWeight: "semi-bold",
                  }}
                >
                  {fullname
                    ? fullname
                      .split(" ")
                      .map((word) => word.charAt(0).toUpperCase())
                      .join("")
                      .substring(0, 2)
                    : "U"}
                </Typography>
              </Box>

              <Typography
                variant="body1"
                sx={{
                  fontStyle: "Open Sans",
                  fontSize: "14px",
                  color: "white",
                  fontWeight: "regular",
                  maxWidth: "150px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={fullname}
              >
                {fullname}
              </Typography>
            </IconButton>

            <StyledMenu
              id="user-profile-menu"
              MenuListProps={{
                "aria-labelledby": "user-profile-button",
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              sx={{
                "& .MuiPaper-root": {
                  backgroundColor: "#000000",
                  padding: 0,
                  minWidth: "133px",
                  borderRadius: "0px",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                  border: "none",
                  marginTop: "0px !important",
                },
                "& .MuiMenu-list": {
                  padding: 0,
                },
              }}
            >
              <MenuItem
                onClick={handleLogOut}
                sx={{
                  backgroundColor: "#000000",
                  "&:hover": {
                    backgroundColor: "#222222",
                  },
                }}
              >
                <Typography
                  sx={{
                    color: "white",
                    fontStyle: "Open Sans",
                    fontSize: "16px",
                    fontWeight: "400",
                  }}
                >
                  Logout
                </Typography>
              </MenuItem>
            </StyledMenu>
          </Box>
        </Box>
      </Box>

      <CustomDialogBoxV2
        open={openLogOutConfirmDialog}
        onClose={() => {
          setOpenLogOutConfirmDialog(false);
        }}
        title={"Logout"}
        successButtonName={"Ok"}
        rejectButtonName={"Cancel"}
        onClickSuccess={handleLogOutConfirm}
        onClickReject={() => setOpenLogOutConfirmDialog(false)}
        actionButtonStyle={"continuous"}
      >
        <DialogContentText sx={{ width: "350px", height: "50px" }}>
          Are you sure you want to logout?
        </DialogContentText>
      </CustomDialogBoxV2>
    </div>
  );
}

export default TopBar;

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 0,
    marginTop: "0px !important",
    minWidth: 140,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "0px",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));
